import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Modal } from "./Guests";
import { Plus, Edit, Trash2, Download } from "lucide-react";

const EMPTY = { company_name: "", contact_name: "", contact_email: "", sponsorship_type: "Banner", episodes_covered: "", rate: "", start_date: "", end_date: "", payment_status: "Pending", deliverables: "", notes: "" };

export default function Sponsors() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const { data } = await api.get("/sponsors");
    setList(data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (editing) await api.put(`/sponsors/${editing}`, form);
    else await api.post("/sponsors", form);
    setOpen(false); setEditing(null); setForm(EMPTY); load();
  };

  const remove = async (id) => { if (window.confirm("Delete sponsor?")) { await api.delete(`/sponsors/${id}`); load(); } };
  const edit = (s) => { setEditing(s.id); setForm({ ...EMPTY, ...s }); setOpen(true); };

  const statusBadge = (s) => s === "Paid" ? "pgm-badge-olive" : s === "Invoiced" ? "pgm-badge-magenta" : "pgm-badge-orange";

  return (
    <div className="pgm-reveal">
      <div className="pgm-micro">◆ Revenue log</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 className="pgm-display" style={{ fontSize: 44 }}>Sponsors</h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="pgm-btn pgm-btn-ghost" onClick={async () => {
            const res = await api.get("/exports/sponsors", { responseType: "blob" });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement("a"); a.href = url; a.download = "sponsors.csv"; document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
          }} data-testid="export-sponsors-btn"><Download size={14} /> Export CSV</button>
          <button className="pgm-btn" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-sponsor-btn"><Plus size={14} /> Add sponsor</button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="pgm-callout" style={{ marginTop: 18 }}><strong>No sponsors yet.</strong> Track deals, payment status, and deliverables here.</div>
      ) : (
        <table className="pgm-table" style={{ marginTop: 18 }} data-testid="sponsors-table">
          <thead><tr><th>Company</th><th>Type</th><th>Episodes</th><th>Rate</th><th>Status</th><th>Dates</th><th></th></tr></thead>
          <tbody>
            {list.map(s => (
              <tr key={s.id} data-testid={`sponsor-row-${s.id}`}>
                <td><strong>{s.company_name}</strong><div style={{ fontSize: 11, opacity: 0.7 }}>{s.contact_name} · {s.contact_email}</div></td>
                <td>{s.sponsorship_type}</td>
                <td>{s.episodes_covered || "—"}</td>
                <td>{s.rate || "—"}</td>
                <td><span className={`pgm-badge ${statusBadge(s.payment_status)}`}>{s.payment_status}</span></td>
                <td style={{ fontSize: 11 }}>{s.start_date || "—"}<br/>{s.end_date}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 10px", fontSize: 10, marginRight: 6 }} onClick={() => edit(s)} data-testid={`sponsor-edit-${s.id}`}><Edit size={11} /></button>
                  <button className="pgm-btn pgm-btn-ghost" style={{ padding: "5px 10px", fontSize: 10, borderColor: "var(--pgm-orange)", color: "var(--pgm-orange)" }} onClick={() => remove(s.id)} data-testid={`sponsor-delete-${s.id}`}><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <Modal onClose={() => { setOpen(false); setEditing(null); }} title={editing ? "Edit sponsor" : "Add sponsor"} width={620}>
          <form onSubmit={submit} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={{ gridColumn: "1 / -1" }}><label className="pgm-label">Company name *</label><input className="pgm-input" required value={form.company_name} onChange={e => setForm({ ...form, company_name: e.target.value })} data-testid="sponsor-company" /></div>
            <div><label className="pgm-label">Contact name</label><input className="pgm-input" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} /></div>
            <div><label className="pgm-label">Contact email</label><input className="pgm-input" type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} /></div>
            <div><label className="pgm-label">Type</label>
              <select className="pgm-select" value={form.sponsorship_type} onChange={e => setForm({ ...form, sponsorship_type: e.target.value })}>
                <option>Banner</option><option>Mention</option><option>Segment</option>
              </select>
            </div>
            <div><label className="pgm-label">Payment status</label>
              <select className="pgm-select" value={form.payment_status} onChange={e => setForm({ ...form, payment_status: e.target.value })}>
                <option>Pending</option><option>Invoiced</option><option>Paid</option>
              </select>
            </div>
            <div><label className="pgm-label">Episodes covered</label><input className="pgm-input" value={form.episodes_covered} onChange={e => setForm({ ...form, episodes_covered: e.target.value })} /></div>
            <div><label className="pgm-label">Rate</label><input className="pgm-input" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} /></div>
            <div><label className="pgm-label">Start date</label><input className="pgm-input" type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><label className="pgm-label">End date</label><input className="pgm-input" type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
            <div style={{ gridColumn: "1 / -1" }}><label className="pgm-label">Deliverables</label><textarea className="pgm-textarea" value={form.deliverables} onChange={e => setForm({ ...form, deliverables: e.target.value })} /></div>
            <div style={{ gridColumn: "1 / -1" }}><label className="pgm-label">Notes</label><textarea className="pgm-textarea" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
              <button type="submit" className="pgm-btn" data-testid="sponsor-save">{editing ? "Update" : "Add sponsor"}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
